import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FavoritesStorage } from '../../lib/utils/favorites';

const Navigation: React.FC = () => {
  const location = useLocation();
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Update favorite count on mount and when returning to page
    const updateCount = () => {
      setFavoriteCount(FavoritesStorage.getFavorites().length);
    };
    
    updateCount();
    
    // Listen for storage events to update count when favorites change in other tabs
    window.addEventListener('storage', updateCount);
    
    // Custom event for same-tab favorite updates
    window.addEventListener('favoritesChanged', updateCount);
    
    return () => {
      window.removeEventListener('storage', updateCount);
      window.removeEventListener('favoritesChanged', updateCount);
    };
  }, []);
  
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  const linkClass = (path: string) => 
    `px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
      isActive(path)
        ? 'bg-gold/15 text-gold border border-gold/40 shadow-lg shadow-gold/20'
        : 'text-gray-300 hover:text-gold-light hover:bg-rift-surface/50'
    }`;

  return (
    <nav className="sticky top-0 z-50 bg-rift-navy/95 backdrop-blur-sm border-b border-gold/20 shadow-lg shadow-black/50">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo - always visible */}
          <Link to="/" className="group flex items-center gap-2 cursor-pointer flex-shrink-0">
            <svg className="w-7 h-7 text-gold group-hover:text-gold-light transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-xl font-bold font-display text-gold group-hover:text-gold-light transition-colors hidden sm:inline tracking-wider">
              Wild Rift Meta
            </span>
            <span className="text-xl font-bold font-display text-gold sm:hidden tracking-wider">
              WR Meta
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <Link to="/" className={linkClass('/')}>Home</Link>
            <Link to="/champions" className={`${linkClass('/champions')} flex items-center gap-2`}>
              Champions
              {favoriteCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-yellow-500 rounded-full">
                  {favoriteCount}
                </span>
              )}
            </Link>
            <Link to="/hidden-gems" className={linkClass('/hidden-gems')}>Hidden Gems</Link>
            <Link to="/patches/compare" className={linkClass('/patches')}>Patches</Link>
            <Link to="/operations" className={linkClass('/operations')}>Operations</Link>
            
            {/* Roles Dropdown */}
            <div className="relative group">
              <button className={`${linkClass('/roles')} flex items-center gap-1 cursor-pointer`}>
                Roles
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="absolute left-0 w-full h-2 top-full" />
              <div className="absolute hidden group-hover:block bg-rift-surface border border-gold/20 rounded-lg shadow-xl shadow-black/50 top-full pt-2 py-2 min-w-[160px] z-50">
                <Link to="/roles/baron" className="block px-4 py-2 hover:bg-gold/10 hover:text-gold text-gray-300 transition-colors">Baron Lane</Link>
                <Link to="/roles/jungle" className="block px-4 py-2 hover:bg-gold/10 hover:text-gold text-gray-300 transition-colors">Jungle</Link>
                <Link to="/roles/mid" className="block px-4 py-2 hover:bg-gold/10 hover:text-gold text-gray-300 transition-colors">Mid Lane</Link>
                <Link to="/roles/dragon" className="block px-4 py-2 hover:bg-gold/10 hover:text-gold text-gray-300 transition-colors">Dragon Lane</Link>
                <Link to="/roles/support" className="block px-4 py-2 hover:bg-gold/10 hover:text-gold text-gray-300 transition-colors">Support</Link>
              </div>
            </div>
          </div>
          
          {/* Right side - API Docs & Theme Toggle */}
          <div className="flex items-center gap-3">
            <a 
              href={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}/docs`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white text-sm transition hidden sm:flex items-center gap-2 cursor-pointer"
              title="API Documentation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden lg:inline">API Docs</span>
            </a>

            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-700 transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-700 space-y-2">
            <Link to="/" className={`block ${linkClass('/')}`} onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/champions" className={`block ${linkClass('/champions')} flex items-center gap-2`} onClick={() => setMobileMenuOpen(false)}>
              Champions
              {favoriteCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-yellow-500 rounded-full">
                  {favoriteCount}
                </span>
              )}
            </Link>
            <Link to="/hidden-gems" className={`block ${linkClass('/hidden-gems')}`} onClick={() => setMobileMenuOpen(false)}>Hidden Gems</Link>
            <Link to="/patches/compare" className={`block ${linkClass('/patches')}`} onClick={() => setMobileMenuOpen(false)}>Patches</Link>
            <Link to="/operations" className={`block ${linkClass('/operations')}`} onClick={() => setMobileMenuOpen(false)}>Operations</Link>
            
            {/* Roles Section */}
            <div className="pt-2 border-t border-gray-700">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-4">Roles</p>
              <Link to="/roles/baron" className="block px-4 py-2 hover:bg-gray-700 text-gray-300 rounded" onClick={() => setMobileMenuOpen(false)}>Baron Lane</Link>
              <Link to="/roles/jungle" className="block px-4 py-2 hover:bg-gray-700 text-gray-300 rounded" onClick={() => setMobileMenuOpen(false)}>Jungle</Link>
              <Link to="/roles/mid" className="block px-4 py-2 hover:bg-gray-700 text-gray-300 rounded" onClick={() => setMobileMenuOpen(false)}>Mid Lane</Link>
              <Link to="/roles/dragon" className="block px-4 py-2 hover:bg-gray-700 text-gray-300 rounded" onClick={() => setMobileMenuOpen(false)}>Dragon Lane</Link>
              <Link to="/roles/support" className="block px-4 py-2 hover:bg-gray-700 text-gray-300 rounded" onClick={() => setMobileMenuOpen(false)}>Support</Link>
            </div>
            
            {/* API Docs for mobile */}
            <a 
              href={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}/docs`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block px-4 py-2 text-gray-400 hover:text-white transition"
            >
              API Documentation
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
